import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Copy, CreditCard, Download, ImagePlus, KeyRound, Loader2, RefreshCw, Save, Trash2, UserPlus } from "lucide-react";
import { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API, apiError, dateFr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LANGUES, appliquerLangue } from "@/i18n";
import i18n from "@/i18n";
import Logo from "@/components/Logo";
import Modal from "@/components/Modal";

export default function Reglages() {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoTick, setLogoTick] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [abo, setAbo] = useState(null);
  const [aboBusy, setAboBusy] = useState(false);
  const [equipe, setEquipe] = useState([]);
  const [nvUtil, setNvUtil] = useState({ nom: "", email: "", password: "" });
  const [addingUser, setAddingUser] = useState(false);
  const [pwd, setPwd] = useState({ ancien: "", nouveau: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [numAuto, setNumAuto] = useState("");
  const [calendly, setCalendly] = useState(null);
  const [supprOpen, setSupprOpen] = useState(false);
  const [supprPwd, setSupprPwd] = useState("");
  const [supprBusy, setSupprBusy] = useState(false);
  const [calBusy, setCalBusy] = useState(false);
  const [gcal, setGcal] = useState(null);
  const [gcalBusy, setGcalBusy] = useState(false);

  useEffect(() => {
    api.get("/abonnement").then((r) => setAbo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin) api.get("/utilisateurs").then((r) => setEquipe(r.data)).catch(() => {});
  }, [isAdmin]);

  async function gererAbonnement() {
    setAboBusy(true);
    try {
      const { data } = await api.post("/abonnement/portail", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(apiError(err, t("reglages.abo_portail_erreur")));
      setAboBusy(false);
    }
  }

  async function ajouterUtilisateur() {
    if (!nvUtil.nom.trim() || !nvUtil.email.trim() || nvUtil.password.length < 6) {
      toast.error(t("reglages.util_requis"));
      return;
    }
    setAddingUser(true);
    try {
      const { data } = await api.post("/utilisateurs", { ...nvUtil, role: "employe" });
      setEquipe([...equipe, data]);
      setNvUtil({ nom: "", email: "", password: "" });
      toast.success(t("reglages.util_cree", { nom: data.nom }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAddingUser(false);
    }
  }

  async function supprimerUtilisateur(u) {
    try {
      await api.delete(`/utilisateurs/${u.id}`);
      setEquipe(equipe.filter((x) => x.id !== u.id));
      toast.success(t("reglages.util_supprime", { nom: u.nom }));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function changerMdp() {
    if (pwd.nouveau.length < 6) {
      toast.error(t("reglages.mdp_min"));
      return;
    }
    setPwdBusy(true);
    try {
      await api.post("/auth/password", pwd);
      setPwd({ ancien: "", nouveau: "" });
      toast.success(t("reglages.mdp_ok"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwdBusy(false);
    }
  }

  useEffect(() => {
    api.get("/entreprise").then((r) => {
      setForm({
        raison_sociale: r.data.raison_sociale || "",
        siret: r.data.siret || "",
        adresse: r.data.adresse || "",
        telephone: r.data.telephone || "",
        email: r.data.email || "",
        mentions_legales: r.data.mentions_legales || "",
        iban: r.data.iban || "",
        bic: r.data.bic || "",
        langue: r.data.langue || "fr",
        prochain_numero_facture: r.data.prochain_numero_facture ?? "",
      });
      setNumAuto(r.data.prochain_numero_auto || "");
      setHasLogo(!!r.data.has_logo);
    });
  }, []);

  useEffect(() => {
    api.get("/calendly/statut").then((r) => setCalendly(r.data)).catch(() => {});
    api.get("/google/statut").then((r) => setGcal(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const retourCal = params.get("calendly");
    if (retourCal) {
      if (retourCal === "connecte") toast.success(t("reglages.calendly_connecte_msg"));
      else toast.error(t("reglages.calendly_erreur"));
    }
    const retourGoogle = params.get("google");
    if (retourGoogle) {
      if (retourGoogle === "connecte") toast.success(t("reglages.google_connecte_msg"));
      else toast.error(t("reglages.google_erreur"));
    }
    if (retourCal || retourGoogle) window.history.replaceState({}, "", "/entreprise");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function connecterCalendly() {
    setCalBusy(true);
    try {
      const { data } = await api.get("/calendly/connect");
      if (!data.configured) {
        toast.error(t("reglages.calendly_non_configure"));
        return;
      }
      window.location.assign(data.url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCalBusy(false);
    }
  }

  async function syncCalendly() {
    setCalBusy(true);
    try {
      const { data } = await api.post("/calendly/sync");
      toast.success(t("reglages.calendly_sync_ok", { count: data.synced }));
      const r = await api.get("/calendly/statut");
      setCalendly(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCalBusy(false);
    }
  }

  async function deconnecterCalendly() {
    try {
      await api.post("/calendly/disconnect");
      setCalendly({ ...calendly, connecte: false });
      toast.success(t("reglages.calendly_deconnecte"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function connecterGoogle() {
    setGcalBusy(true);
    try {
      const { data } = await api.get("/google/connect");
      if (!data.configured) {
        toast.error(t("reglages.google_non_configure"));
        return;
      }
      window.location.assign(data.url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGcalBusy(false);
    }
  }

  async function deconnecterGoogle() {
    try {
      await api.post("/google/disconnect");
      setGcal({ ...gcal, connecte: false });
      toast.success(t("reglages.google_deconnecte"));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function copier(texte) {
    try {
      await navigator.clipboard.writeText(texte);
      toast.success(t("reglages.lien_copie"));
    } catch {
      toast.error(t("reglages.copie_erreur"));
    }
  }

  async function exporterDonnees() {
    try {
      await downloadFile("/entreprise/export", "mastoras_export.zip");
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function supprimerCompte() {
    if (!supprPwd) return;
    setSupprBusy(true);
    try {
      await api.delete("/entreprise/compte", { data: { mot_de_passe: supprPwd } });
      toast.success(t("reglages.compte_supprime"));
      setTimeout(() => { window.location.href = "/login"; }, 1200);
    } catch (e) {
      toast.error(apiError(e));
      setSupprBusy(false);
    }
  }

  async function uploadLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("reglages.logo_taille"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/entreprise/logo", fd);
      setHasLogo(true);
      setLogoTick(Date.now());
      toast.success(t("reglages.logo_ok"));
    } catch (err) {
      toast.error(apiError(err, t("reglages.logo_erreur")));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function removeLogo() {
    try {
      await api.delete("/entreprise/logo");
      setHasLogo(false);
      toast.success(t("reglages.logo_supprime"));
    } catch (err) {
      toast.error(apiError(err));
    }
  }

  const annee = new Date().getFullYear();
  const numeroAffiche = form && form.prochain_numero_facture
    ? `F-${annee}-${String(Number(form.prochain_numero_facture) || 0).padStart(4, "0")}`
    : numAuto;

  if (!form) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    setBusy(true);
    try {
      await api.put("/entreprise", {
        ...form,
        prochain_numero_facture: form.prochain_numero_facture === "" || form.prochain_numero_facture == null
          ? null : Number(form.prochain_numero_facture),
      });
      appliquerLangue(form.langue);
      toast.success(i18n.getFixedT(form.langue)("reglages.enregistre"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 max-w-2xl" data-testid="page-entreprise">
      <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("reglages.titre")}</h1>
      <p className="text-stone-500 text-sm -mt-2">{t("reglages.sous_titre")}</p>

      <div className="panel" data-testid="bloc-abonnement">
        <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.abonnement")}</h2>
        {!abo ? (
          <p className="text-stone-500 text-sm">{t("commun.chargement")}</p>
        ) : abo.statut === "fondateur" ? (
          <p className="text-sm text-stone-600" data-testid="reglages-abo-fondateur">{t("reglages.abo_fondateur")}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-stone-600 flex-1 min-w-[200px]" data-testid="reglages-abo-statut">
              {abo.statut === "actif"
                ? `${t("reglages.abo_actif")}${abo.periode_fin ? ` — ${t("reglages.abo_renouvellement", { date: dateFr(abo.periode_fin) })}` : ""}.`
                : abo.statut === "essai"
                  ? t("reglages.abo_essai", { count: abo.jours_restants })
                  : t("reglages.abo_bloque")}
            </p>
            {isAdmin && (abo.stripe_customer ? (
              <button onClick={gererAbonnement} disabled={aboBusy} className="btn-secondary" data-testid="btn-portail-stripe">
                {aboBusy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
                {t("reglages.abo_gerer_court")}
              </button>
            ) : (
              <Link to="/abonnement" className="btn-accent" data-testid="lien-voir-abonnement">
                {t("reglages.abo_voir")}
              </Link>
            ))}
          </div>
        )}
      </div>

      {isAdmin ? (
        <>
      <div className="panel space-y-4" data-testid="bloc-langue">
        <div>
          <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.langue")}</h2>
          <p className="text-stone-500 text-sm mb-3">{t("reglages.langue_note")}</p>
          <select className="input-field max-w-xs" value={form.langue} onChange={set("langue")}
                  data-testid="select-langue">
            {LANGUES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="panel" data-testid="bloc-logo-entreprise">
        <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.logo")}</h2>
        <p className="text-stone-500 text-sm mb-4">{t("reglages.logo_texte")}</p>
        <div className="flex flex-wrap items-center gap-4">
          {hasLogo ? (
            <img src={`${API}/entreprise/logo?t=${logoTick}`} alt={t("reglages.logo")}
                 className="w-16 h-16 rounded-lg border border-[#E6DFD1] bg-white object-contain p-1"
                 data-testid="apercu-logo" />
          ) : (
            <Logo size={64} iconSize={34} />
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden"
                   onChange={uploadLogo} data-testid="input-logo-entreprise" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary"
                    data-testid="btn-upload-logo">
              {uploading ? <Loader2 className="animate-spin" size={18} /> : <ImagePlus size={18} />}
              {hasLogo ? t("reglages.logo_changer") : t("reglages.logo_ajouter")}
            </button>
            {hasLogo && (
              <button onClick={removeLogo} className="btn-secondary !text-[#A32E20]" data-testid="btn-retirer-logo">
                <Trash2 size={18} /> {t("reglages.logo_supprimer")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="panel" data-testid="bloc-numerotation">
        <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.numero_titre")}</h2>
        <p className="text-stone-500 text-sm mb-3">{t("reglages.numero_note")}</p>
        <label className="block text-sm font-medium mb-1.5">{t("reglages.numero_label")}</label>
        <input className="input-field max-w-xs" type="number" min="1" step="1"
               placeholder={t("reglages.numero_placeholder")}
               value={form.prochain_numero_facture}
               onChange={set("prochain_numero_facture")} data-testid="input-prochain-numero-facture" />
        <p className="text-xs text-stone-500 mt-2" data-testid="numero-prochain-auto">
          {t("reglages.numero_auto", { numero: numeroAffiche })}
        </p>
      </div>

      <div className="panel" data-testid="bloc-calendly">
        <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.calendly_titre")}</h2>
        <p className="text-stone-500 text-sm mb-3">{t("reglages.calendly_note")}</p>
        {!calendly ? (
          <p className="text-stone-500 text-sm">{t("commun.chargement")}</p>
        ) : !calendly.configure ? (
          <p className="text-sm text-stone-500" data-testid="calendly-non-configure">{t("reglages.calendly_non_configure")}</p>
        ) : !calendly.connecte ? (
          <button onClick={connecterCalendly} disabled={calBusy} className="btn-secondary" data-testid="btn-calendly-connect">
            {calBusy ? <Loader2 className="animate-spin" size={18} /> : <Calendar size={18} />}
            {t("reglages.calendly_connecter")}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-stone-600 flex-1 min-w-[200px]" data-testid="calendly-statut">
                {t("reglages.calendly_connecte_a", { compte: calendly.compte })}
                {calendly.derniere_sync && ` — ${t("reglages.calendly_derniere_sync", { date: dateFr(calendly.derniere_sync) })}`}
              </p>
              <button onClick={syncCalendly} disabled={calBusy} className="btn-secondary" data-testid="btn-calendly-sync">
                {calBusy ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                {t("reglages.calendly_sync")}
              </button>
              <button onClick={deconnecterCalendly} className="btn-secondary !text-[#A32E20]" data-testid="btn-calendly-disconnect">
                <Trash2 size={18} /> {t("reglages.calendly_deconnecter")}
              </button>
            </div>
            {calendly.lien && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#F5F0E6] border border-[#E6DFD1] px-3 py-2"
                   data-testid="calendly-lien-bloc">
                <a href={calendly.lien} target="_blank" rel="noreferrer"
                   className="text-sm text-[#8A5A2B] font-medium underline truncate flex-1 min-w-[180px]"
                   data-testid="calendly-lien">{calendly.lien}</a>
                <button onClick={() => copier(calendly.lien)} className="btn-secondary !py-1.5"
                        data-testid="btn-calendly-copier-lien">
                  <Copy size={16} /> {t("reglages.copier_lien")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="panel" data-testid="bloc-google-cal">
        <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.google_titre")}</h2>
        <p className="text-stone-500 text-sm mb-3">{t("reglages.google_note")}</p>
        {!gcal ? (
          <p className="text-stone-500 text-sm">{t("commun.chargement")}</p>
        ) : !gcal.configure ? (
          <p className="text-sm text-stone-500" data-testid="google-non-configure">{t("reglages.google_non_configure")}</p>
        ) : !gcal.connecte ? (
          <button onClick={connecterGoogle} disabled={gcalBusy} className="btn-secondary" data-testid="btn-google-connect">
            {gcalBusy ? <Loader2 className="animate-spin" size={18} /> : <Calendar size={18} />}
            {t("reglages.google_connecter")}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-stone-600 flex-1 min-w-[200px]" data-testid="google-statut">
                {t("reglages.google_connecte_a", { compte: gcal.compte })}
              </p>
              <button onClick={deconnecterGoogle} className="btn-secondary !text-[#A32E20]" data-testid="btn-google-disconnect">
                <Trash2 size={18} /> {t("reglages.google_deconnecter")}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[#F5F0E6] border border-[#E6DFD1] px-3 py-2"
                 data-testid="google-calendrier-actif">
              <p className="text-sm text-stone-600 flex-1 min-w-[180px]">
                {t("reglages.google_calendrier_actif", { compte: gcal.compte })}
              </p>
              {gcal.compte && gcal.compte.includes("@") && (
                <button onClick={() => copier(gcal.compte)} className="btn-secondary !py-1.5"
                        data-testid="btn-google-copier-adresse">
                  <Copy size={16} /> {t("reglages.copier_adresse")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="panel space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">{t("reglages.raison_sociale")}</label>
          <input className="input-field" value={form.raison_sociale} onChange={set("raison_sociale")}
                 data-testid="input-raison-sociale" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">{t("reglages.siret")}</label>
          <input className="input-field" value={form.siret} onChange={set("siret")} data-testid="input-siret" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">{t("reglages.adresse")}</label>
          <input className="input-field" value={form.adresse} onChange={set("adresse")} data-testid="input-adresse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("reglages.telephone")}</label>
            <input className="input-field" value={form.telephone} onChange={set("telephone")} data-testid="input-telephone" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">{t("reglages.email")}</label>
            <input className="input-field" type="email" value={form.email} onChange={set("email")} data-testid="input-email-entreprise" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">{t("reglages.rib")}</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <input className="input-field" placeholder={t("reglages.iban")} value={form.iban}
                     onChange={set("iban")} data-testid="input-iban" />
            </div>
            <div>
              <input className="input-field" placeholder={t("reglages.bic")} value={form.bic}
                     onChange={set("bic")} data-testid="input-bic" />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">{t("reglages.mentions")}</label>
          <textarea className="input-field !h-32 py-3" value={form.mentions_legales} onChange={set("mentions_legales")}
                    placeholder={t("reglages.mentions_placeholder")}
                    data-testid="input-mentions-legales" />
        </div>
        <button onClick={save} disabled={busy} className="btn-accent" data-testid="btn-sauvegarder-entreprise">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {t("reglages.enregistrer")}
        </button>
      </div>
        </>
      ) : (
        <div className="panel" data-testid="info-role-employe">
          <p className="text-sm text-stone-600">{t("reglages.employe_info")}</p>
        </div>
      )}

      {isAdmin && (
        <div className="panel space-y-4" data-testid="bloc-equipe">
          <div>
            <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.equipe")}</h2>
            <p className="text-stone-500 text-sm">{t("reglages.equipe_note")}</p>
          </div>
          <div className="divide-y divide-[#E6DFD1]">
            {equipe.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2.5" data-testid={`utilisateur-${u.id}`}>
                <div>
                  <div className="font-medium text-sm">{u.nom}</div>
                  <div className="text-stone-500 text-xs">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2 py-1 rounded bg-[#EFEAE0] text-[#8A5A2B]">
                    {u.role === "admin" ? t("reglages.role_admin") : t("reglages.role_employe")}
                  </span>
                  {u.id !== user?.id && (
                    <button onClick={() => supprimerUtilisateur(u)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FBE4E1] text-[#A32E20]"
                            data-testid={`btn-supprimer-utilisateur-${u.id}`}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input className="input-field" placeholder={t("reglages.util_nom")} value={nvUtil.nom}
                   onChange={(e) => setNvUtil({ ...nvUtil, nom: e.target.value })} data-testid="input-employe-nom" />
            <input className="input-field" type="email" placeholder={t("reglages.util_email")} value={nvUtil.email}
                   onChange={(e) => setNvUtil({ ...nvUtil, email: e.target.value })} data-testid="input-employe-email" />
            <input className="input-field" placeholder={t("reglages.util_mdp")} value={nvUtil.password}
                   onChange={(e) => setNvUtil({ ...nvUtil, password: e.target.value })} data-testid="input-employe-password" />
          </div>
          <button onClick={ajouterUtilisateur} disabled={addingUser} className="btn-secondary" data-testid="btn-ajouter-employe">
            {addingUser ? <Loader2 className="animate-spin" size={18} /> : <UserPlus size={18} />}
            {t("reglages.util_creer")}
          </button>
        </div>
      )}

      <div className="panel space-y-4" data-testid="bloc-mon-compte">
        <div>
          <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.mon_compte")}</h2>
          <p className="text-stone-500 text-sm">{user?.nom} · {user?.email}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input type="password" className="input-field" placeholder={t("reglages.mdp_ancien")} value={pwd.ancien}
                 onChange={(e) => setPwd({ ...pwd, ancien: e.target.value })} data-testid="input-mdp-actuel" />
          <input type="password" className="input-field" placeholder={t("reglages.mdp_nouveau")} value={pwd.nouveau}
                 onChange={(e) => setPwd({ ...pwd, nouveau: e.target.value })} data-testid="input-mdp-nouveau" />
        </div>
        <button onClick={changerMdp} disabled={pwdBusy} className="btn-secondary" data-testid="btn-changer-mdp">
          {pwdBusy ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={18} />}
          {t("reglages.mdp_changer")}
        </button>
      </div>

      {user?.role === "admin" && (
        <div className="panel space-y-4" data-testid="bloc-donnees">
          <div>
            <h2 className="font-serif font-semibold text-lg mb-1">{t("reglages.donnees")}</h2>
            <p className="text-stone-500 text-sm">{t("reglages.donnees_note")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={exporterDonnees} className="btn-secondary" data-testid="btn-export-donnees">
              <Download size={18} /> {t("reglages.export_donnees")}
            </button>
            <button onClick={() => setSupprOpen(true)} className="btn-secondary !text-[#A32E20]" data-testid="btn-supprimer-compte">
              <Trash2 size={18} /> {t("reglages.supprimer_compte")}
            </button>
          </div>
        </div>
      )}

      <Modal open={supprOpen} onClose={() => { setSupprOpen(false); setSupprPwd(""); }} title={t("reglages.suppr_titre")} testid="modal-supprimer-compte">
        <div className="space-y-4">
          <p className="text-sm text-stone-600">{t("reglages.suppr_texte")}</p>
          <input type="password" className="input-field" placeholder={t("reglages.suppr_mdp_label")}
                 value={supprPwd} onChange={(e) => setSupprPwd(e.target.value)}
                 data-testid="input-suppr-mdp" />
          <button onClick={supprimerCompte} disabled={!supprPwd || supprBusy}
                  className="btn-accent w-full !bg-[#A32E20]" data-testid="btn-confirmer-suppression">
            {supprBusy ? <Loader2 className="animate-spin" size={18} /> : t("reglages.suppr_confirmer")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
